<script lang="ts">
  import { onMount, type Snippet, untrack } from 'svelte';

  import Card from '$shared/atoms/Card/Card.svelte';
  import Alert from './partials/Alert.svelte';
  import Container from './partials/Container.svelte';
  import Footer from './partials/Footer.svelte';
  import Header from './partials/Header.svelte';
  import Meta from './partials/Meta.svelte';
  import Tab from './partials/Tab.svelte';
  import AlertInfo from './partials/AlertInfo.svelte';
  import { EntityTab } from './EntityViewProp';
  import type { EntityViewType } from '$shared/components/types';

  interface Props {
    options: EntityViewType;
    readonly?: boolean;
    actions?: Snippet | undefined;
    customAction?: Snippet | undefined;
    resultMeta?: Snippet | undefined;
    providedInfoMeta?: Snippet | undefined;
    onalert?: (alert: any) => void;
    onedit?: () => void;
    onremove?: () => void;
    onreverify?: () => void;
    onoption?: (option: any) => void;
    onevent?: (payload: any) => void;
    ontab?: (tab: any) => void;
  }

  let {
    options,
    readonly = false,
    actions = undefined,
    customAction = undefined,
    resultMeta = undefined,
    providedInfoMeta = undefined,
    onalert,
    onedit,
    onremove,
    onreverify,
    onoption,
    onevent,
    ontab
  }: Props = $props();
  export const setTab = (tab: string): void => {
    activeTab = tab;
  };

  let activeTab: string = $state('');

  let DefaultTabs = $derived({
    [EntityTab.Result]: {
      id: EntityTab.Result,
      label: 'Verified Results',
      hide: options?.tab?.hideResult
    },
    [EntityTab.ProvidedInfo]: {
      id: EntityTab.ProvidedInfo,
      label: 'Provided Info',
      hide: options?.tab?.hideProvidedInfo
    }
  });

  let tabs = $derived(Object.values(DefaultTabs).filter((tab) => !tab.hide));
  let showFooter = $derived(
    options?.footer &&
      (!options.footer.onlyProvidedInfo || activeTab === DefaultTabs[EntityTab.ProvidedInfo].id)
  );

  onMount(() => {
    const tabList = tabs;
    const current = untrack(() => activeTab);
    const defaultTab = untrack(() => options?.tab?.defaultActiveTab);
    if (!current) {
      activeTab = defaultTab || tabList?.first()?.id || '';
    } else if (!tabList.find((tab) => tab.id === current)) {
      activeTab = tabList?.first()?.id || '';
    }
  });
</script>

<div data-cy={`${options?.name}-entity`} class="entity-view">
  <Alert alert={options?.alert} {onalert} />
  <Card>
    <Header
      {...options?.header}
      name={options?.name}
      {readonly}
      loading={options?.loading || options?.processing}
      {customAction}
      {onedit}
      {onremove}
      {onreverify}
      {onoption}
      {onevent}
    >
      {@render actions?.()}
    </Header>
    <Tab bind:activeTab {tabs} hideTab={options?.hideTab} options={options?.tab?.options} {ontab}>
      <AlertInfo {...options?.tab?.alert} {activeTab} />
      <Meta
        {...options?.tab?.meta}
        hasProvidedInfoMeta={!!providedInfoMeta && activeTab === EntityTab.ProvidedInfo}
      />
      <Container
        {...options?.tab?.results}
        name={options?.name}
        loading={options?.loading}
        hideTab={options?.hideTab}
        hasVerification={options?.hasVerification}
        verificationProcessingLabel={options?.verificationProcessingLabel}
        {activeTab}
        {providedInfoMeta}
        {onevent}
      >
        {@render resultMeta?.()}
      </Container>
    </Tab>
  </Card>
  {#if showFooter}
    <Footer {...options?.footer} />
  {/if}
</div>

<style></style>
